/**
 * Ecranul de condus (Etapele 5–8): mașina node-beam cu suspensie, anvelope, frâne, motor, turbo și cutie,
 * desenată cu modelul 3D legat de noduri (caroseria se deformează) și cu roțile care virează și se rotesc.
 * Controale: touch (pedale analogice, volan glisant sau înclinare), tastatură, gamepad. HUD, telemetrie,
 * cronometre 0–100 și 402 m, sunet, tuning (ECU, piese, dyno, diagnoză), reparare.
 */
import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CarSound, soundPreset } from '../audio/carSound';
import { loadConceptScene, nodeMaterial, shapeConcept } from '../cars/concept';
import { createLoader, type ModelSource } from '../cars/loader';
import { VEHICLES } from '../data/catalog';
import type { VehicleDefinition } from '../data/types';
import { bindVertices, nodeTexture, skinnedPosition, writeNodes } from '../lab/skin';
import { powertrainInfo, type PowertrainInfo } from '../tuning/info';
import { loadCalibration } from '../tuning/store';
import type { Ecu, Parts } from '../tuning/types';
import type { LabCmd, LabMsg } from '../worker/body.worker';
import { openEcuPanel } from './ecuPanel';
import { brandLogo } from './logos';

type Level = 'mobile' | 'desktop';
type Cam = 'chase' | 'cockpit' | 'hood' | 'orbit';
const LEVEL_KEY = 'nb-level';
const BODIES = import.meta.glob('../../data/bodies/*.json', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>;
const WHEELS = ['FL', 'FR', 'RL', 'RR'] as const;
const SURFACES: Array<[string, number]> = [['Asfalt uscat', 1], ['Asfalt ud', 0.75], ['Pietriș', 0.6], ['Iarbă', 0.5], ['Noroi', 0.4], ['Zăpadă', 0.3], ['Gheață', 0.1]];

export interface DriveOptions { models: ModelSource; createWorker?: () => Worker; onClose?: () => void }

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

async function bodyJson(id: string, level: Level): Promise<string> {
  const key = Object.keys(BODIES).find((k) => k.endsWith(`/${id}-${level}.json`));
  if (!key) throw new Error(`lipsește structura ${id}-${level}`);
  return BODIES[key]();
}

export function createDrive(root: HTMLElement, opts: DriveOptions) {
  let worker: Worker | null = null;
  let renderer: THREE.WebGPURenderer | null = null;
  let scene: THREE.Scene, camera: THREE.PerspectiveCamera, controls: OrbitControls;
  let ro: ResizeObserver | null = null;
  let car: VehicleDefinition = VEHICLES[1];
  let level: Level | null = null;
  let info: PowertrainInfo | null = null;
  let nodes = 0, rest: Float32Array = new Float32Array(0), latest: Float32Array | null = null;
  let sab: { seq: Int32Array; data: Float32Array } | null = null;
  let curTex: THREE.DataTexture | null = null, restTex: THREE.DataTexture | null = null;
  let carGroup: THREE.Group | null = null;
  let wheelGroups: Array<{ steer: THREE.Group; spin: THREE.Group }> = [];
  let mounts = { fl: 0, fr: 0, rl: 0, rr: 0 }, hubs: number[] = [];
  let wheelState: Float32Array = new Float32Array(20), tele: Float32Array | null = null;
  let ecu: { current: Ecu; stock: Ecu; parts: Parts } | null = null;
  let world: THREE.Object3D[] = [];
  let ground: THREE.Group;
  let cam: Cam = 'chase';
  let sound: CarSound | null = null, muted = false;
  let brakeMats: THREE.MeshPhysicalNodeMaterial[] = [];
  let tilt = false, aids = true, autoShift = true, surface = 0, menu: '' | 'car' | 'more' = '';
  let lastPlastic = 0, impact = 0;
  const input = { throttle: 0, brake: 0, handbrake: 0, steer: 0, steerTarget: 0, touchSteer: false };
  const keys = new Set<string>();
  const dynoWait = new Map<string, (rows: Float32Array) => void>();
  const history: Array<[number, number, number, number]> = [];
  const timers = { armed: true, t0: 0, dist: 0, running: false, t100: 0, t402: 0, v402: 0, best100: 0 };
  let showTele = false;
  const loader = createLoader();
  const tmpQ = new THREE.Quaternion(), tmpQ2 = new THREE.Quaternion(), tmpM = new THREE.Matrix4();
  const camPos = new THREE.Vector3(), camLook = new THREE.Vector3();
  // pentru depanare din consolă: window.__drive
  const dbg: Record<string, unknown> = {};
  (window as unknown as { __drive: unknown }).__drive = dbg;

  root.innerHTML = `<div class="drive-canvas"></div>
    <div class="drive-hud"></div>
    <canvas class="drive-tele" width="360" height="120" hidden></canvas>
    <div class="drive-top"></div>
    <div class="drive-menu" hidden></div>
    <div class="drive-steer" aria-label="Volan"><div class="knob"></div></div>
    <div class="drive-pedals">
      <div class="pedal brake" data-pedal="brake"><span>Frână</span><i></i></div>
      <div class="pedal gas" data-pedal="throttle"><span>Accelerație</span><i></i></div>
    </div>
    <div class="drive-btns">
      <button data-a="up">▲</button><button data-a="down">▼</button>
      <button data-a="hb" class="hb">Frână de mână</button>
    </div>
    <section class="garage ecu" hidden></section>`;
  const view = root.querySelector('.drive-canvas') as HTMLElement;
  const hud = root.querySelector('.drive-hud') as HTMLElement;
  const top = root.querySelector('.drive-top') as HTMLElement;
  const menuEl = root.querySelector('.drive-menu') as HTMLElement;
  const teleCv = root.querySelector('.drive-tele') as HTMLCanvasElement;
  const steerEl = root.querySelector('.drive-steer') as HTMLElement;
  const knob = steerEl.querySelector('.knob') as HTMLElement;
  const ecuEl = root.querySelector('.ecu') as HTMLElement;

  const send = (m: LabCmd) => worker?.postMessage(m);

  function topHtml(): string {
    const b = (a: string, label: string, on = false) => `<button data-a="${a}" aria-pressed="${on}">${label}</button>`;
    return `${b('car', `${brandLogo(car.brandId)} ${esc(car.model)}`)}${b('cam', { chase: 'Cameră: spate', cockpit: 'Cameră: interior', hood: 'Cameră: capotă', orbit: 'Cameră: liberă' }[cam])}
      ${b('tune', 'Tuning')}${b('repair', 'Repară')}${b('more', '⋯')}${b('close', '✕')}`;
  }

  function menuHtml(): string {
    if (menu === 'car') return `<div class="lab-cars">${VEHICLES.map((v) => `<button data-car="${v.id}" aria-pressed="${v.id === car.id}"><span class="logo sm">${brandLogo(v.brandId)}</span><span>${esc(v.model)} <small class="muted">${esc(v.variant)}</small></span></button>`).join('')}</div>`;
    const b = (a: string, label: string, on = false) => `<button data-a="${a}" aria-pressed="${on}">${label}</button>`;
    return `<div class="row">${b('auto', autoShift ? 'Cutie: automată' : 'Cutie: manuală', autoShift)}${b('aids', aids ? 'ABS/TC: pornite' : 'ABS/TC: oprite', aids)}${b('tilt', tilt ? 'Direcție: înclinare' : 'Direcție: glisor', tilt)}${b('engine', 'Pornește motorul')}</div>
      <div class="row">${SURFACES.map(([n], i) => b(`surf:${i}`, n, i === surface)).join('')}</div>
      <div class="row">${b('ramp', 'Rampă în față')}${b('wall', 'Perete în față')}${b('tele', 'Telemetrie', showTele)}${b('sound', muted ? 'Sunet: oprit' : 'Sunet: pornit', !muted)}</div>
      <div class="row"><span class="muted">Nivel fizic</span>${b('level:mobile', 'Mobil', level === 'mobile')}${b('level:desktop', 'Desktop', level === 'desktop')}</div>
      <p class="muted small">Modelul 3D: „Car Concept” (Khronos Group, CC BY 4.0, după un model CC0 de Unity Fan), remodelat după proporțiile fiecărei mașini. Sunetul: sinteza pe cilindri din simulatorul tău vechi, condusă de fizică.</p>
      <p class="muted small">Tastatură: W/S sau săgețile, A/D, Space frână de mână, E/Q trepte, C cameră, R repară, I pornește motorul. Gamepad: RT/LT pedale, stick stânga direcția, RB/LB trepte, A frână de mână, Y repară.</p>`;
  }

  async function chooseLevel(): Promise<Level> {
    const saved = localStorage.getItem(LEVEL_KEY);
    if (saved === 'mobile' || saved === 'desktop') return saved;
    hud.innerHTML = '<div class="big">Benchmark de 3 secunde…</div>';
    const json = await bodyJson('wendland-pendler-19tdp', 'desktop');
    const rate = await new Promise<number>((resolve) => {
      const h = (e: MessageEvent<LabMsg>) => { if (e.data.type === 'result' && e.data.kind === 'bench') { worker!.removeEventListener('message', h); resolve(e.data.value); } };
      worker!.addEventListener('message', h);
      send({ type: 'bench', json, seconds: 3 });
    });
    const lv: Level = rate >= 3000 ? 'desktop' : 'mobile';
    localStorage.setItem(LEVEL_KEY, lv);
    return lv;
  }

  function clearCar(): void {
    for (const o of [carGroup]) {
      if (!o) continue;
      o.traverse((c) => { const m = c as THREE.Mesh; m.geometry?.dispose(); const mat = m.material as THREE.Material | THREE.Material[] | undefined; (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => x.dispose()); });
      o.removeFromParent();
    }
    carGroup = null;
    wheelGroups = [];
    curTex?.dispose(); restTex?.dispose();
  }

  async function loadBody(): Promise<void> {
    if (!level) level = await chooseLevel();
    hud.innerHTML = '<div class="big">Se încarcă mașina…</div>';
    const json = await bodyJson(car.id, level);
    const def = JSON.parse(json) as { nodes: Array<{ pos: number[]; group: string; tag: string }>; refs: { mountFl: number; mountFr: number; mountRl: number; mountRr: number }; wheels: Array<{ hub: number }> };
    info = powertrainInfo(def as never);
    clearCar();
    for (const o of world) o.removeFromParent();
    world = [];
    nodes = def.nodes.length;
    mounts = { fl: def.refs.mountFl, fr: def.refs.mountFr, rl: def.refs.mountRl, rr: def.refs.mountRr };
    hubs = def.wheels.map((w) => w.hub);
    rest = new Float32Array(nodes * 3);
    def.nodes.forEach((n, i) => rest.set(n.pos, i * 3));
    latest = rest.slice();
    const iso = typeof SharedArrayBuffer !== 'undefined' && self.crossOriginIsolated;
    const buf = iso ? new SharedArrayBuffer(16 + nodes * 6 * 4) : undefined;
    sab = buf ? { seq: new Int32Array(buf, 0, 1), data: new Float32Array(buf, 16, nodes * 6) } : null;
    ecu = null;
    send({ type: 'load', json, sab: buf, drive: true });
    send({ type: 'autoShift', on: autoShift });
    send({ type: 'aids', on: aids });
    send({ type: 'surface', mu: SURFACES[surface][1] });
    // tuning-ul salvat pentru mașina asta
    const saved = await loadCalibration(car.id);
    if (saved) { send({ type: 'setEcu', json: JSON.stringify(saved.ecu) }); send({ type: 'setParts', json: JSON.stringify(saved.parts) }); }

    curTex = nodeTexture(nodes, rest);
    restTex = nodeTexture(nodes, rest);
    // modelul detaliat, remodelat după proporțiile mașinii; caroseria se deformează după noduri, roțile urmează butucii
    const parts = shapeConcept(await loadConceptScene(loader), car);
    const bodyCand = def.nodes.map((n, i) => (n.group === 'wheel' ? -1 : i)).filter((i) => i >= 0);
    const posNode = skinnedPosition(curTex, restTex);
    const mats = new Map<THREE.Material, THREE.MeshPhysicalNodeMaterial>();
    const paint = car.design.colors[0].hex;
    carGroup = new THREE.Group();
    brakeMats = [];
    wheelGroups = WHEELS.map(() => { const steer = new THREE.Group(), spin = new THREE.Group(); steer.add(spin); carGroup!.add(steer); return { steer, spin }; });
    for (const part of parts) {
      if (part.wheel >= 0) {
        const mesh = new THREE.Mesh(part.geometry, part.material);
        mesh.castShadow = true;
        (part.spin ? wheelGroups[part.wheel].spin : wheelGroups[part.wheel].steer).add(mesh);
        continue;
      }
      const p = part.geometry.getAttribute('position').array as Float32Array;
      const b = bindVertices(p, rest, bodyCand, 4);
      part.geometry.setAttribute('nbIndex', new THREE.BufferAttribute(b.index, 4));
      part.geometry.setAttribute('nbWeight', new THREE.BufferAttribute(b.weight, 4));
      let m = mats.get(part.material);
      if (!m) {
        m = nodeMaterial(part.material, paint, posNode);
        if (/brakelight/i.test(part.material.name)) { m.emissive.set(0xff1a0a); brakeMats.push(m); }
        mats.set(part.material, m);
      }
      const sm = new THREE.Mesh(part.geometry, m);
      sm.castShadow = true;
      sm.frustumCulled = false;
      carGroup.add(sm);
    }
    scene.add(carGroup);
    Object.assign(timers, { armed: true, running: false, t100: 0, t402: 0, v402: 0, dist: 0 });
    top.innerHTML = topHtml();
    void sound?.dispose();
    const e = car.engine;
    sound = new CarSound(soundPreset({
      cylinders: e.cylinders, displacementL: e.displacementCc / 1000, turbo: e.aspiration !== 'na', diesel: e.fuel === 'diesel', carburetor: e.fuelSystem === 'carburetor',
      idleRpm: e.idleRpm, redlineRpm: e.redlineRpm, limiterRpm: e.limiter.rpm, peakRpm: Math.round((e.maxTorqueRpm[0] + e.maxPowerRpm) / 2), boreStroke: e.boreMm / e.strokeMm,
      body: car.body.type === 'suv' ? 'suv' : car.body.type === 'sedan' ? 'sedan' : 'hatch'
    }));
    sound.setVolume(muted ? 0 : 0.8);
  }

  function onMsg(e: MessageEvent<LabMsg>): void {
    const m = e.data;
    if (m.type === 'frame') latest = m.positions;
    else if (m.type === 'drive') { wheelState = m.wheels; tele = m.tele; }
    else if (m.type === 'ecu') ecu = { current: JSON.parse(m.current), stock: JSON.parse(m.stock), parts: JSON.parse(m.parts) };
    else if (m.type === 'dyno') { dynoWait.get(m.tag)?.(m.rows); dynoWait.delete(m.tag); }
    else if (m.type === 'stats') { if (m.plastic > lastPlastic + 3) impact = Math.min(1, (m.plastic - lastPlastic) / 60); lastPlastic = m.plastic; }
    else if (m.type === 'ramp' || m.type === 'wall') {
      if (m.z === null) return;
      const mesh = m.type === 'ramp'
        ? (() => { const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute([-2.4, 0, m.z, 2.4, 0, m.z, 2.4, 1.2, m.z + 8, -2.4, 1.2, m.z + 8, -2.4, 0, m.z + 8, 2.4, 0, m.z + 8], 3)); g.setIndex([0, 3, 1, 1, 3, 2, 0, 4, 3, 1, 2, 5, 3, 4, 5, 3, 5, 2]); g.computeVertexNormals(); return new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0xc9a227, roughness: 0.8, side: THREE.DoubleSide })); })()
        : (() => { const w = new THREE.Mesh(new THREE.BoxGeometry(8, 5, 0.4), new THREE.MeshStandardMaterial({ color: 0x8a8f99, roughness: 0.9 })); w.position.set(0, 2.0, m.z + 0.2); return w; })();
      mesh.receiveShadow = true;
      mesh.castShadow = true;
      scene.add(mesh);
      world.push(mesh);
    } else if (m.type === 'error') hud.innerHTML = `<div class="bad">Eroare: ${esc(m.message)}</div>`;
  }

  // ---------- comenzi ----------
  function pointerPedal(el: HTMLElement): void {
    const key = el.dataset.pedal as 'throttle' | 'brake';
    const fill = el.querySelector('i') as HTMLElement;
    const set = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const v = Math.min(1, Math.max(0, (r.bottom - e.clientY) / r.height));
      input[key] = v;
      fill.style.height = `${v * 100}%`;
    };
    el.addEventListener('pointerdown', (e) => { el.setPointerCapture(e.pointerId); set(e); sound?.resume(); });
    el.addEventListener('pointermove', (e) => { if (el.hasPointerCapture(e.pointerId)) set(e); });
    const up = (e: PointerEvent) => { if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId); input[key] = 0; fill.style.height = '0'; };
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }
  root.querySelectorAll<HTMLElement>('.pedal').forEach(pointerPedal);
  steerEl.addEventListener('pointerdown', (e) => { steerEl.setPointerCapture(e.pointerId); input.touchSteer = true; steerMove(e); });
  steerEl.addEventListener('pointermove', (e) => { if (steerEl.hasPointerCapture(e.pointerId)) steerMove(e); });
  const steerUp = (e: PointerEvent) => { if (steerEl.hasPointerCapture(e.pointerId)) steerEl.releasePointerCapture(e.pointerId); input.touchSteer = false; input.steerTarget = 0; };
  steerEl.addEventListener('pointerup', steerUp);
  steerEl.addEventListener('pointercancel', steerUp);
  function steerMove(e: PointerEvent): void {
    const r = steerEl.getBoundingClientRect();
    // spre stânga = virare la stânga (pozitiv)
    input.steerTarget = Math.min(1, Math.max(-1, -((e.clientX - r.left) / r.width - 0.5) * 2));
  }
  const hbBtn = root.querySelector('[data-a="hb"]') as HTMLElement;
  hbBtn.addEventListener('pointerdown', () => { input.handbrake = 1; });
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) hbBtn.addEventListener(ev, () => { input.handbrake = 0; });
  function onTilt(e: DeviceOrientationEvent): void {
    if (!tilt || e.beta === null) return;
    const angle = (screen.orientation?.angle ?? (window as unknown as { orientation?: number }).orientation ?? 90) as number;
    const sign = angle === 270 || angle === -90 ? 1 : -1;
    input.steerTarget = Math.min(1, Math.max(-1, (sign * e.beta) / 28));
  }
  const keyDown = (e: KeyboardEvent) => {
    if (root.hidden || !ecuEl.hidden) return;
    keys.add(e.code);
    if (e.code === 'KeyE' || e.code === 'ShiftRight') send({ type: 'shift', delta: 1 });
    if (e.code === 'KeyQ' || e.code === 'ControlRight') send({ type: 'shift', delta: -1 });
    if (e.code === 'KeyC') cycleCam();
    if (e.code === 'KeyR') send({ type: 'repair' });
    if (e.code === 'KeyI') send({ type: 'startEngine' });
    if (e.code.startsWith('Arrow') || e.code === 'Space') e.preventDefault();
    sound?.resume();
  };
  const keyUp = (e: KeyboardEvent) => keys.delete(e.code);
  let padPrev: boolean[] = [];

  function readInputs(dt: number, speed: number): void {
    const k = (...c: string[]) => c.some((x) => keys.has(x));
    let thr = input.throttle, brk = input.brake, hb = input.handbrake, target = input.steerTarget;
    let kbSteer = false;
    if (k('KeyW', 'ArrowUp')) thr = 1;
    if (k('KeyS', 'ArrowDown')) brk = 1;
    if (k('Space')) hb = 1;
    if (k('KeyA', 'ArrowLeft')) { target = 1; kbSteer = true; }
    if (k('KeyD', 'ArrowRight')) { target = kbSteer ? 0 : -1; kbSteer = true; }
    for (const gp of navigator.getGamepads?.() ?? []) {
      if (!gp) continue;
      const ax = gp.axes[0] ?? 0;
      if (Math.abs(ax) > 0.08) target = -ax;
      thr = Math.max(thr, gp.buttons[7]?.value ?? 0);
      brk = Math.max(brk, gp.buttons[6]?.value ?? 0);
      if (gp.buttons[0]?.pressed) hb = 1;
      const pressed = gp.buttons.map((b) => b.pressed);
      const edge = (i: number) => pressed[i] && !padPrev[i];
      if (edge(5)) send({ type: 'shift', delta: 1 });
      if (edge(4)) send({ type: 'shift', delta: -1 });
      if (edge(3)) send({ type: 'repair' });
      if (edge(2)) cycleCam();
      padPrev = pressed;
    }
    // viteza de rotire a volanului: limitată la tastatură și touch, mai mică la viteză mare
    const rate = (kbSteer ? 2.2 : 4) / (1 + speed / 25);
    const d = target - input.steer;
    input.steer += Math.sign(d) * Math.min(Math.abs(d), rate * dt);
    send({ type: 'controls', throttle: thr, brake: brk, handbrake: hb, steer: input.steer });
    knob.style.left = `${50 - input.steer * 45}%`;
  }

  function cycleCam(): void {
    const order: Cam[] = ['chase', 'cockpit', 'hood', 'orbit'];
    cam = order[(order.indexOf(cam) + 1) % order.length];
    controls.enabled = cam === 'orbit';
    top.innerHTML = topHtml();
  }

  // ---------- desenare ----------
  const v3 = (p: Float32Array, i: number) => new THREE.Vector3(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
  function frame(now: number): void {
    const dt = Math.min(0.05, (now - lastFrame) / 1000);
    lastFrame = now;
    let p = latest;
    if (sab) {
      const seq = Atomics.load(sab.seq, 0);
      if (seq > 0) p = sab.data.subarray((seq & 1) * nodes * 3, (seq & 1) * nodes * 3 + nodes * 3);
    }
    const speedKmh = tele ? tele[0] : 0;
    readInputs(dt, Math.abs(speedKmh) / 3.6);
    if (p && curTex && carGroup) {
      writeNodes(curTex.image.data as Float32Array, p, nodes);
      curTex.needsUpdate = true;
      const fl = v3(p, mounts.fl), fr = v3(p, mounts.fr), rl = v3(p, mounts.rl), rr = v3(p, mounts.rr);
      const fwd = fl.clone().add(fr).sub(rl).sub(rr).normalize();
      const left = fl.clone().add(rl).sub(fr).sub(rr);
      left.sub(fwd.clone().multiplyScalar(left.dot(fwd))).normalize();
      const up = new THREE.Vector3().crossVectors(fwd, left);
      tmpM.makeBasis(left, up, fwd);
      const qBody = new THREE.Quaternion().setFromRotationMatrix(tmpM);
      hubs.forEach((h, k) => {
        const wg = wheelGroups[k];
        if (!wg) return;
        wg.steer.position.copy(v3(p!, h));
        wg.steer.quaternion.copy(qBody).multiply(tmpQ.setFromAxisAngle(new THREE.Vector3(0, 1, 0), wheelState[k * 5]));
        wg.spin.quaternion.copy(tmpQ2.setFromAxisAngle(new THREE.Vector3(1, 0, 0), wheelState[k * 5 + 1]));
      });
      const center = fl.clone().add(fr).add(rl).add(rr).multiplyScalar(0.25);
      dbg.center = center; dbg.fwd = fwd; dbg.dt = dt;
      ground.position.set(Math.round(center.x / 10) * 10, 0, Math.round(center.z / 10) * 10);
      sun.position.set(center.x + 8, 14, center.z + 6);
      sun.target.position.copy(center);
      // camera
      if (cam === 'chase') {
        const back = fwd.clone().setY(0).normalize();
        camPos.copy(center).addScaledVector(back, -6.2).add(new THREE.Vector3(0, 2.2, 0));
        camera.position.lerp(camPos, Math.min(1, dt * 5));
        camLook.copy(center).addScaledVector(back, 2).add(new THREE.Vector3(0, 0.8, 0));
        camera.lookAt(camLook);
      } else if (cam === 'cockpit' || cam === 'hood') {
        const local = cam === 'cockpit' ? new THREE.Vector3(0.36, 1.12, -0.25) : new THREE.Vector3(0, 1.25, 1.1);
        camera.position.copy(center).addScaledVector(left, local.x).addScaledVector(up, local.y - 0.45).addScaledVector(fwd, local.z);
        camera.up.copy(up);
        camera.lookAt(camera.position.clone().addScaledVector(fwd, 10).addScaledVector(up, -0.6));
        camera.up.set(0, 1, 0);
      } else {
        controls.target.lerp(center, Math.min(1, dt * 4));
        controls.update();
      }
      camera.fov = cam === 'cockpit' ? 72 : 55;
      camera.updateProjectionMatrix();
    }
    if (tele) updateHud(dt);
    renderer!.render(scene, camera);
  }
  let lastFrame = performance.now();
  let hudT = 0;

  function updateHud(dt: number): void {
    const t = tele!;
    const kmh = t[0], rpm = t[1], gear = t[2];
    // cronometrele: 0–100 km/h și 402 m, de pe loc
    if (Math.abs(kmh) < 0.5 && !timers.running) timers.armed = true;
    if (timers.armed && kmh > 0.5) Object.assign(timers, { armed: false, running: true, t0: performance.now(), dist: 0, t100: 0, t402: 0 });
    if (timers.running) {
      timers.dist += (kmh / 3.6) * dt;
      const el = (performance.now() - timers.t0) / 1000;
      if (!timers.t100 && kmh >= 100) { timers.t100 = el; if (!timers.best100 || el < timers.best100) timers.best100 = el; }
      if (!timers.t402 && timers.dist >= 402) { timers.t402 = el; timers.v402 = kmh; }
      if (kmh < 1 || el > 60) timers.running = false;
    }
    history.push([rpm, kmh, t[3], t[4]]);
    if (history.length > 600) history.shift();
    const slip = Math.max(...[0, 1, 2, 3].map((k) => Math.max(Math.abs(wheelState[k * 5 + 2]), Math.abs(wheelState[k * 5 + 3]) * 0.8)));
    sound?.update({ rpm, throttle: t[4], load: Math.max(0.05, t[4]) * Math.min(1.2, Math.max(0, t[10]) / Math.max(1, car.engine.maxTorqueNm) + 0.15), boost: t[3], speed: Math.abs(kmh) / 3.6, slip, running: t[15] > 0.5, impact });
    for (const m of brakeMats) m.emissiveIntensity = 0.4 + (input.brake > 0.05 || keys.has('KeyS') || keys.has('ArrowDown') ? 3.5 : 0);
    impact = 0;
    hudT += dt;
    if (showTele) drawTele();
    if (hudT < 0.1) return;
    hudT = 0;
    const red = info?.redlineRpm ?? 6500, lim = info?.limiterRpm ?? 7000;
    const g = gear < 0 ? 'R' : gear === 0 ? 'N' : String(gear);
    const codes = (t[16] | 0) !== 0;
    hud.innerHTML = `<div class="speed">${Math.abs(kmh).toFixed(0)}<small>km/h</small></div>
      <div class="gear">${g}</div>
      <div class="rpm"><i style="width:${Math.min(100, (rpm / lim) * 100)}%" class="${rpm > red ? 'red' : ''}"></i><span>${rpm.toFixed(0)} rpm</span></div>
      ${info?.turbo ? `<div class="mini">Turbo ${t[3].toFixed(2)} bar</div>` : ''}
      <div class="mini">Lichid ${t[6].toFixed(0)} °C · EGT ${t[7].toFixed(0)} °C · ${t[11].toFixed(0)} kW · g ${t[17].toFixed(2)} / ${t[18].toFixed(2)}</div>
      ${t[15] < 0.5 ? `<div class="bad">${t[21] ? 'Motorul s-a smuls din suporți' : 'Motor oprit'} <button data-a="engine">Pornește</button></div>` : ''}
      ${codes ? '<div class="warn">⚠ Verifică motorul (Tuning → Diagnoză)</div>' : ''}
      ${t[6] > 112 ? '<div class="bad">Supraîncălzire</div>' : ''}
      <div class="mini">0–100: ${timers.t100 ? `${timers.t100.toFixed(2)} s` : timers.running ? '…' : '—'}${timers.best100 ? ` (record ${timers.best100.toFixed(2)})` : ''} · 402 m: ${timers.t402 ? `${timers.t402.toFixed(2)} s la ${timers.v402.toFixed(0)} km/h` : '—'}</div>`;
  }

  function drawTele(): void {
    const g = teleCv.getContext('2d')!;
    const W = teleCv.width, H = teleCv.height;
    g.clearRect(0, 0, W, H);
    g.fillStyle = 'rgba(14,18,24,.75)';
    g.fillRect(0, 0, W, H);
    const series: Array<[number, number, string]> = [[0, info?.limiterRpm ?? 7000, '#ffb000'], [1, 250, '#5aa9ff'], [2, 2.5, '#7be38a'], [3, 1, '#ff6b5a']];
    for (const [idx, max, color] of series) {
      g.strokeStyle = color;
      g.lineWidth = 1.5;
      g.beginPath();
      history.forEach((h, i) => { const x = (i / 600) * W, y = H - (Math.max(0, h[idx]) / max) * (H - 8) - 4; if (i) g.lineTo(x, y); else g.moveTo(x, y); });
      g.stroke();
    }
    g.fillStyle = '#eef2f6';
    g.font = '11px system-ui';
    g.fillText('rpm  km/h  bar  accelerație (10 s)', 6, 13);
  }

  // ---------- meniuri ----------
  function openTune(): void {
    if (!ecu || !info) return;
    openEcuPanel(ecuEl, {
      carId: car.id, carName: `${car.model} ${car.variant}`, info, stock: ecu.stock, current: ecu.current, parts: ecu.parts,
      apply: (e, p) => { send({ type: 'setEcu', json: JSON.stringify(e) }); send({ type: 'setParts', json: JSON.stringify(p) }); },
      dyno: (e, p) => new Promise((resolve) => { const tag = Math.random().toString(36).slice(2); dynoWait.set(tag, resolve); send({ type: 'dyno', ecu: JSON.stringify(e), parts: JSON.stringify(p), tag }); }),
      telemetry: () => tele,
      onClose: () => { send({ type: 'getEcu' }); }
    });
  }

  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('.ecu')) return;
    const c = t.closest('[data-car]') as HTMLElement | null;
    if (c) { car = VEHICLES.find((v) => v.id === c.dataset.car)!; menu = ''; menuEl.hidden = true; void loadBody().catch((err) => { hud.innerHTML = `<div class="bad">Eroare: ${esc(String(err))}</div>`; }); return; }
    const b = t.closest('[data-a]') as HTMLElement | null;
    if (!b) return;
    const [a, arg] = (b.dataset.a ?? '').split(':');
    sound?.resume();
    switch (a) {
      case 'up': send({ type: 'shift', delta: 1 }); break;
      case 'down': send({ type: 'shift', delta: -1 }); break;
      case 'close': close(); return;
      case 'cam': cycleCam(); break;
      case 'tune': openTune(); break;
      case 'repair': send({ type: 'repair' }); break;
      case 'car': menu = menu === 'car' ? '' : 'car'; break;
      case 'more': menu = menu === 'more' ? '' : 'more'; break;
      case 'auto': autoShift = !autoShift; send({ type: 'autoShift', on: autoShift }); break;
      case 'aids': aids = !aids; send({ type: 'aids', on: aids }); break;
      case 'engine': send({ type: 'startEngine' }); break;
      case 'ramp': send({ type: 'ramp' }); break;
      case 'wall': send({ type: 'wall' }); break;
      case 'tele': showTele = !showTele; teleCv.hidden = !showTele; break;
      case 'sound': muted = !muted; sound?.setVolume(muted ? 0 : 0.8); break;
      case 'surf': surface = Number(arg); send({ type: 'surface', mu: SURFACES[surface][1] }); break;
      case 'level': localStorage.setItem(LEVEL_KEY, arg); level = arg as Level; menu = ''; void loadBody(); break;
      case 'tilt': {
        const D = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
        if (!tilt && D.requestPermission) void D.requestPermission().then((r) => { tilt = r === 'granted'; menuEl.innerHTML = menuHtml(); });
        else tilt = !tilt;
        if (!tilt) input.steerTarget = 0;
        break;
      }
    }
    if (menu) { menuEl.hidden = false; menuEl.innerHTML = menuHtml(); } else menuEl.hidden = true;
  });

  let sun: THREE.DirectionalLight;
  async function open(): Promise<void> {
    root.hidden = false;
    top.innerHTML = topHtml();
    worker = opts.createWorker ? opts.createWorker() : new Worker(new URL('../worker/body.worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', onMsg);
    renderer = new THREE.WebGPURenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.shadowMap.enabled = true;
    view.appendChild(renderer.domElement);
    scene = new THREE.Scene();
    dbg.scene = () => scene;
    dbg.camera = () => camera;
    dbg.carGroup = () => carGroup;
    scene.background = new THREE.Color(0x9fc3e8);
    scene.fog = new THREE.Fog(0x9fc3e8, 120, 420);
    {
      // cer simplu ca hartă de mediu (reflexii pe vopsea și geamuri)
      const cv = document.createElement('canvas');
      cv.width = 256; cv.height = 128;
      const g = cv.getContext('2d')!;
      const grad = g.createLinearGradient(0, 0, 0, 128);
      grad.addColorStop(0, '#7fb2ea'); grad.addColorStop(0.48, '#e6eef6'); grad.addColorStop(0.52, '#6b6f74'); grad.addColorStop(1, '#3a3d40');
      g.fillStyle = grad; g.fillRect(0, 0, 256, 128);
      const env = new THREE.CanvasTexture(cv);
      env.mapping = THREE.EquirectangularReflectionMapping;
      env.colorSpace = THREE.SRGBColorSpace;
      scene.environment = env;
    }
    camera = new THREE.PerspectiveCamera(55, 1, 0.05, 600);
    camera.position.set(6, 3, -6);
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enabled = false;
    scene.add(new THREE.HemisphereLight(0xe8f0ff, 0x4a4438, 1.4));
    sun = new THREE.DirectionalLight(0xffffff, 2.4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    Object.assign(sun.shadow.camera, { left: -10, right: 10, top: 10, bottom: -10 });
    scene.add(sun, sun.target);
    ground = new THREE.Group();
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(1200, 1200), new THREE.MeshStandardMaterial({ color: 0x5b6168, roughness: 0.95 }));
    plane.rotation.x = -Math.PI / 2;
    plane.receiveShadow = true;
    const grid = new THREE.GridHelper(400, 40, 0x7b838c, 0x6a7178);
    grid.position.y = 0.01;
    ground.add(plane, grid);
    scene.add(ground);
    // conuri de reper
    for (let i = 0; i < 30; i++) {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.5, 12), new THREE.MeshStandardMaterial({ color: 0xff6a00 }));
      cone.position.set(((i % 2) * 2 - 1) * 6, 0.25, 20 + i * 15);
      cone.castShadow = true;
      scene.add(cone);
    }
    const resize = () => { const w = view.clientWidth, h = view.clientHeight; if (w < 2 || h < 2) return; renderer!.setSize(w, h); camera.aspect = w / h; camera.updateProjectionMatrix(); };
    ro = new ResizeObserver(resize);
    ro.observe(view);
    await renderer.init();
    resize();
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('deviceorientation', onTilt);
    renderer.setAnimationLoop(frame);
    try { await loadBody(); } catch (e) { hud.innerHTML = `<div class="bad">Eroare: ${esc(String(e))}</div>`; }
  }

  function close(): void {
    root.hidden = true;
    renderer?.setAnimationLoop(null);
    clearCar();
    for (const o of world) o.removeFromParent();
    ro?.disconnect();
    renderer?.dispose();
    renderer?.domElement.remove();
    renderer = null;
    worker?.terminate();
    worker = null;
    void sound?.dispose();
    sound = null;
    window.removeEventListener('keydown', keyDown);
    window.removeEventListener('keyup', keyUp);
    window.removeEventListener('deviceorientation', onTilt);
    opts.onClose?.();
  }

  return { open: () => { void open(); }, close };
}
